import Feature from 'ol/Feature'
import { LineString, Point } from 'ol/geom'
import VectorLayer from 'ol/layer/Vector'
import { fromLonLat } from 'ol/proj'
import VectorSource from 'ol/source/Vector'
import CircleStyle from 'ol/style/Circle.js'
import Fill from 'ol/style/Fill'
import Stroke from 'ol/style/Stroke'
import Style from 'ol/style/Style'
import { useEffect, useState } from 'react'
import useMapStore from 'store/map'
import { ICleaningZonePoint, IRoute, IVisualizationSettings } from 'utils/interfaces'

interface Props {
  cleaningRoutes: IRoute[]
  startingPoints: ICleaningZonePoint[]
  endingPoints: ICleaningZonePoint[]
  visualSettings: IVisualizationSettings
}

function CleaningRoutesLayer({ cleaningRoutes, startingPoints, endingPoints, visualSettings }: Props) {
  const map = useMapStore(s => s.map)
  const [layer, setLayer] = useState<VectorLayer<VectorSource<Feature<LineString> | Feature<Point>>> | null>(null)

  useEffect(() => {
    layer?.getSource()?.clear()
    const features = getFeatures()
    layer?.getSource()?.addFeatures(features)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cleaningRoutes, layer, startingPoints, endingPoints])

  useEffect(() => {
    const source = new VectorSource({
      features: getFeatures(),
    })

    const layer = new VectorLayer({
      source,
      properties: {
        name: 'Cleaning Routes',
        showInLayerList: true,
      },
      zIndex: 2,
      style: feature => {
        const isStartingPoint = String(feature.getId()).includes('_starting_point')
        return new Style(
          feature?.getGeometry()?.getType() === 'Point'
            ? {
                image: new CircleStyle({
                  radius: 6,
                  fill: new Fill({
                    color: isStartingPoint ? 'white' : '#9747FF',
                  }),
                  stroke: new Stroke({
                    color: isStartingPoint ? '#9747FF' : 'white',
                    width: 2,
                  }),
                }),
              }
            : {
                stroke: new Stroke({
                  color: visualSettings.cleaning_route_color,
                  width: visualSettings.cleaning_route_width,
                }),
              }
        )
      },
      opacity: visualSettings.cleaning_route_opacity,
    })
    map?.addLayer(layer)
    setLayer(layer)
    return () => {
      map?.removeLayer(layer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map])

  const getFeatures = () => {
    const features = [
      ...cleaningRoutes.map(route => {
        const feature = new Feature({
          // @ts-ignore
          geometry: new LineString(route.geometry.coordinates[0]).transform('EPSG:4326', 'EPSG:3857'),
          ...route.properties,
        })
        feature.setId(route.id)
        return feature
      }),
      ...startingPoints.map(point => {
        const feature = new Feature({
          geometry: new Point(fromLonLat([point.geometry.coordinates[0], point.geometry.coordinates[1]])),
        })
        feature.setId(point.id)
        return feature
      }),
      ...endingPoints.map(point => {
        const feature = new Feature({
          geometry: new Point(fromLonLat([point.geometry.coordinates[0], point.geometry.coordinates[1]])),
        })
        feature.setId(point.id)
        return feature
      }),
    ]
    cleaningRoutes.forEach(cleaningRoute => {
      const startingPointExisting = startingPoints.find(
        startingPoint => startingPoint.properties?.zone_id === cleaningRoute.properties?.zone_id
      )
      if (!startingPointExisting) {
        const feature = new Feature({
          geometry: new Point(fromLonLat(cleaningRoute.geometry.coordinates[0][0])),
        })
        feature.setId(cleaningRoute.id + '_starting_point')
        features.push(feature)
      }
    })

    cleaningRoutes.forEach(cleaningRoute => {
      const endingPointExisting = endingPoints.find(
        endingPoint => endingPoint.properties?.zone_id === cleaningRoute.properties?.zone_id
      )
      if (!endingPointExisting) {
        const lastLineIndex = cleaningRoute.geometry.coordinates.length - 1
        const lastCoordIndex = cleaningRoute.geometry.coordinates[lastLineIndex].length - 1
        const feature = new Feature({
          geometry: new Point(fromLonLat(cleaningRoute.geometry.coordinates[lastLineIndex][lastCoordIndex])),
        })
        feature.setId(cleaningRoute.id + '_ending_point')
        features.push(feature)
      }
    })
    return features
  }

  useEffect(() => {
    layer?.setStyle(feature => {
      const isStartingPoint = String(feature.getId()).includes('_starting_point')
      return new Style(
        feature?.getGeometry()?.getType() === 'Point'
          ? {
              image: new CircleStyle({
                radius: 6,
                fill: new Fill({
                  color: isStartingPoint ? 'white' : '#9747FF',
                }),
                stroke: new Stroke({
                  color: isStartingPoint ? '#9747FF' : 'white',
                  width: 2,
                }),
              }),
            }
          : {
              stroke: new Stroke({
                color: visualSettings.cleaning_route_color,
                width: visualSettings.cleaning_route_width,
              }),
            }
      )
    })
    layer?.setOpacity(visualSettings.cleaning_route_opacity)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visualSettings])

  return null
}

export default CleaningRoutesLayer
